<template>
  <div class="clinic-dashboard content-wrapper">

    <!-- ===== Header ===== -->
    <div class="d-flex justify-content-between align-items-start mb-2 flex-wrap" style="gap: 12px">
      <div>
        <div class="font-weight-bold">ภาพรวมคลินิก · ทุกสาขา</div>
        <small class="text-muted">
          {{ branchCount }} สาขา
          <template v-if="c">
            · ข้อมูล <b class="text-dark">{{ rangeLabel }}</b>
            <span v-if="comparisonLabel">
              · เทียบกับ <span class="text-primary-dark">{{ comparisonLabel }}</span>
            </span>
          </template>
        </small>
      </div>

      <!-- ===== Range picker + comparison toggle ===== -->
      <div class="d-flex align-items-end flex-wrap" style="gap: 10px">
        <!-- View type: month / year -->
        <div class="cd-seg">
          <button
            type="button"
            class="cd-seg-btn"
            :class="{ on: viewType === 'month' }"
            :disabled="loading"
            @click="setViewType('month')"
          >รายเดือน</button>
          <button
            type="button"
            class="cd-seg-btn"
            :class="{ on: viewType === 'year' }"
            :disabled="loading"
            @click="setViewType('year')"
          >รายปี</button>
        </div>

        <!-- Period select -->
        <b-form-select
          v-if="viewType === 'month'"
          v-model="selectedMonth"
          :options="monthOptions"
          size="sm"
          class="cd-period-select"
          :disabled="loading"
        ></b-form-select>
        <b-form-select
          v-else
          v-model="selectedYear"
          :options="yearOptions"
          size="sm"
          class="cd-period-select"
          :disabled="loading"
        ></b-form-select>

        <!-- Comparison toggle (client-side only) -->
        <div class="d-flex align-items-center" style="gap: 6px">
          <span class="small text-muted font-weight-semibold">เทียบ</span>
          <div class="cd-seg cd-seg--ghost">
            <button
              type="button"
              class="cd-seg-btn"
              :class="{ on: comparison === 'prevPeriod' }"
              @click="comparison = 'prevPeriod'"
            >ช่วงก่อน</button>
            <button
              type="button"
              class="cd-seg-btn"
              :class="{ on: comparison === 'prevYear' }"
              @click="comparison = 'prevYear'"
            >ปีก่อน</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading && !c" class="text-center p-5">
      <b-spinner />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="text-center p-5">
      <p class="text-danger">โหลดข้อมูลไม่สำเร็จ</p>
      <b-button size="sm" variant="outline-primary" @click="load">ลองใหม่</b-button>
    </div>

    <template v-else-if="c">

      <!-- ===== Alerts ===== -->
      <div
        v-if="c.alerts && c.alerts.length"
        class="d-flex align-items-center mb-3"
        style="gap: 8px; overflow-x: auto; flex-wrap: wrap"
      >
        <span class="small font-weight-semibold text-muted flex-shrink-0">ต้องดูวันนี้:</span>
        <span
          v-for="(alert, i) in c.alerts"
          :key="i"
          class="alert-chip"
          :class="alertChipClass(alert)"
          @click="openDrawer(alert.branchUrl, branchNameByUrl[alert.branchUrl])"
        >
          {{ alertIcon(alert) }} {{ alertLabel(alert) }}
        </span>
      </div>

      <!-- ===== KPI row ===== -->
      <b-row v-if="c.summary" class="mb-3">

        <!-- รายได้รวม -->
        <b-col cols="12" sm="6" lg="3" class="mb-2">
          <b-card class="kpi-card h-100" body-class="p-3">
            <div class="label-muted small font-weight-semibold text-uppercase">รายได้รวม</div>
            <div class="d-flex align-items-baseline mt-2" style="gap: 5px">
              <span class="kpi-num font-weight-bold">{{ formatNumber(c.summary.revenue.value) }}</span>
              <span class="small text-muted">฿</span>
            </div>
            <div class="small text-muted mt-1">เงินเก็บจริง (cash collected)</div>
            <trend-badge class="mt-2" :pct="revenuePct" :label="comparisonTag" />
          </b-card>
        </b-col>

        <!-- ฿/เคส -->
        <b-col cols="12" sm="6" lg="3" class="mb-2">
          <b-card class="kpi-card h-100" body-class="p-3">
            <div class="label-muted small font-weight-semibold text-uppercase">฿ ต่อใบเสร็จ</div>
            <div class="d-flex align-items-baseline mt-2" style="gap: 5px">
              <span class="kpi-num font-weight-bold">{{ formatNumber(c.summary.perCase.value) }}</span>
              <span class="small text-muted">฿</span>
            </div>
            <div class="small text-muted mt-1">รายได้ ÷ ใบเสร็จ</div>
            <trend-badge class="mt-2" :pct="perCasePct" :label="comparisonTag" />
          </b-card>
        </b-col>

        <!-- ผู้ป่วย -->
        <b-col cols="12" sm="6" lg="3" class="mb-2">
          <b-card class="kpi-card h-100" body-class="p-3">
            <div class="label-muted small font-weight-semibold text-uppercase">ผู้ป่วย</div>
            <div class="d-flex align-items-baseline mt-2" style="gap: 5px">
              <span class="kpi-num font-weight-bold">{{ formatNumber(c.summary.patients.total) }}</span>
              <span class="small text-muted">คน</span>
            </div>
            <div class="small text-muted mt-1">
              <span class="text-green font-weight-bold">ผู้ป่วยใหม่ {{ formatNumber(c.summary.patients.new) }} คน</span>
            </div>
            <trend-badge class="mt-2" :pct="c.summary.patientsPrevPct" label="vs ช่วงก่อน" />
          </b-card>
        </b-col>

        <!-- No-show -->
        <b-col cols="12" sm="6" lg="3" class="mb-2">
          <b-card class="kpi-card h-100" body-class="p-3">
            <div class="label-muted small font-weight-semibold text-uppercase">No-show</div>
            <div class="d-flex align-items-baseline mt-2" style="gap: 5px">
              <span class="kpi-num font-weight-bold">{{ noShowPctText }}</span>
              <span class="small text-muted">%</span>
            </div>
            <div class="small text-muted mt-1">{{ formatNumber(c.summary.noShow.count) }} นัด</div>
          </b-card>
        </b-col>

      </b-row>

      <!-- ===== Trend + Case Mix ===== -->
      <b-row class="mb-3" style="min-height: 280px">

        <!-- Revenue trend -->
        <b-col md="7" class="mb-2 mb-md-0">
          <b-card class="h-100 d-flex flex-column" body-class="d-flex flex-column p-3">
            <div class="font-weight-bold small">แนวโน้มรายได้ · รวมทุกสาขา</div>
            <div class="label-muted small">{{ trendSubLabel }}</div>
            <div class="flex-fill mt-2" style="position: relative; min-height: 220px" v-if="trendChartData">
              <bar-chart :chart-data="trendChartData" :options="trendChartOptions" />
            </div>
            <div v-else class="text-center text-muted small py-4">ไม่มีข้อมูลแนวโน้ม</div>
          </b-card>
        </b-col>

        <!-- Case-mix donut -->
        <b-col md="5" class="mb-2 mb-md-0">
          <b-card class="h-100 d-flex flex-column" body-class="d-flex flex-column p-3">
            <div class="font-weight-bold small">สัดส่วนหัตถการ · ตามหมวด</div>
            <div class="label-muted small">สัดส่วนรายได้ตามหมวด</div>
            <div
              v-if="caseMixItems && caseMixItems.length"
              class="d-flex align-items-center flex-fill mt-2"
              style="gap: 14px; min-width: 0"
            >
              <div style="position: relative; height: 150px; width: 150px; flex: 0 0 150px; max-width: 150px">
                <pie-chart :chart-data="caseMixChartData" :options="caseMixOptions" />
              </div>
              <div class="flex-fill" style="min-width: 0">
                <div
                  v-for="(item, i) in caseMixItems"
                  :key="i"
                  class="d-flex justify-content-between align-items-center py-1"
                >
                  <span class="d-flex align-items-center small" style="gap: 6px; min-width: 0">
                    <span class="legend-dot" :style="{ background: item.color }"></span>
                    <span class="text-truncate">{{ item.category }}</span>
                  </span>
                  <span class="small font-weight-bold flex-shrink-0">{{ item.pct }}%</span>
                </div>
              </div>
            </div>
            <div v-else class="text-muted small text-center py-4">ไม่มีข้อมูล case mix</div>
          </b-card>
        </b-col>

      </b-row>

      <!-- ===== Top treatments + Referral sources ===== -->
      <b-row class="mb-3">

        <!-- Top treatments -->
        <b-col md="7" class="mb-2 mb-md-0">
          <b-card class="h-100" body-class="p-3">
            <div class="font-weight-bold small mb-2">
              รายการรักษาทำเงินสูงสุด
              <span class="font-weight-normal text-muted">· ตามรายได้</span>
            </div>
            <div
              v-if="c.topTreatments && c.topTreatments.length"
              class="d-flex flex-column"
              style="gap: 11px"
            >
              <div
                v-for="(t, i) in c.topTreatments"
                :key="t.treatmentId || i"
                class="treat-row"
              >
                <div class="treat-rank">{{ i + 1 }}</div>
                <div style="min-width: 0">
                  <div class="d-flex justify-content-between align-items-baseline small mb-1" style="gap: 10px">
                    <span class="text-truncate">{{ t.name }}</span>
                    <span class="font-weight-bold flex-shrink-0">
                      ฿{{ formatNumber(t.revenue) }}
                      <span class="text-muted font-weight-normal" style="font-size: 11.5px">{{ formatNumber(t.count) }} เคส</span>
                    </span>
                  </div>
                  <div class="treat-track">
                    <div class="treat-fill" :style="{ width: treatBarWidth(t.revenue) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-muted small text-center py-4">ไม่มีข้อมูลรายการรักษา</div>
          </b-card>
        </b-col>

        <!-- Referral sources -->
        <b-col md="5" class="mb-2 mb-md-0">
          <b-card class="h-100" body-class="p-3">
            <div class="font-weight-bold small mb-2">
              แหล่งที่มาผู้ป่วยใหม่
              <span class="font-weight-normal text-muted">· ในช่วงที่เลือก</span>
            </div>
            <div
              v-if="c.referralSources && c.referralSources.length"
              class="d-flex flex-column"
              style="gap: 11px"
            >
              <div
                v-for="(src, i) in c.referralSources"
                :key="i"
                class="d-flex align-items-center small"
                style="gap: 10px"
              >
                <span class="referral-name text-truncate">{{ src.source }}</span>
                <div class="referral-bar-track flex-fill">
                  <div
                    class="referral-bar-fill"
                    :style="{ width: referralBarWidth(src.count) + '%' }"
                  ></div>
                </div>
                <span class="font-weight-bold flex-shrink-0" style="width: 36px; text-align: right">{{ src.count }}</span>
              </div>
            </div>
            <div v-else class="text-muted small text-center py-4">ไม่มีข้อมูลแหล่งที่มา</div>
          </b-card>
        </b-col>

      </b-row>

      <!-- ===== Branches comparison table ===== -->
      <b-card no-body class="mb-3" v-if="sortedBranches.length">
        <div class="d-flex justify-content-between align-items-center p-3 pb-2">
          <div class="font-weight-bold small">เปรียบเทียบรายสาขา</div>
          <span class="small text-muted">เรียงตามรายได้ในช่วง · คลิกเพื่อดูรายละเอียด</span>
        </div>
        <div class="table-responsive">
          <table class="cd-branch-table">
            <thead>
              <tr>
                <th class="text-left">สาขา</th>
                <th>รายได้</th>
                <th>฿/เคส</th>
                <th>ผู้ป่วย (ใหม่)</th>
                <th>No-show</th>
                <th>ใช้ยูนิต</th>
                <th class="text-left">หมอทำเงินสูงสุด</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="branch in sortedBranches"
                :key="branch.branchUrl"
                class="cd-branch-row"
                @click="openDrawer(branch.branchUrl, branch.name)"
              >
                <td class="text-left">
                  <div class="bname">{{ branch.name }}</div>
                  <div class="bsub">/{{ branch.branchUrl }}</div>
                </td>
                <td>
                  <span class="money">฿{{ formatNumber(branch.revenue.value) }}</span>
                  <span
                    v-if="branchRevenuePct(branch) != null"
                    class="branch-delta"
                    :class="branchRevenuePct(branch) >= 0 ? 'text-green' : 'text-red'"
                  >
                    {{ branchRevenuePct(branch) >= 0 ? '▲+' : '▼' }}{{ Math.abs(branchRevenuePct(branch)).toFixed(1) }}%
                  </span>
                </td>
                <td class="money">฿{{ formatNumber(branch.perCase) }}</td>
                <td>
                  {{ formatNumber(branch.patients) }}
                  <span class="bsub">({{ formatNumber(branch.newPatients) }})</span>
                </td>
                <td>
                  <span class="pill" :class="noShowPillClass(branch.noShowPct)">
                    {{ (branch.noShowPct || 0).toFixed(1) }}%
                  </span>
                </td>
                <td>
                  <span v-if="branch.utilPct != null" class="pill" :class="utilPillClass(branch.utilPct)">
                    {{ branch.utilPct }}%
                  </span>
                  <span v-else class="text-muted">–</span>
                </td>
                <td class="text-left">
                  <span v-if="branch.topDoctor" class="doc">
                    <span class="doc-av">{{ doctorInitials(branch.topDoctor.name) }}</span>
                    {{ branch.topDoctor.name }}
                  </span>
                  <span v-else class="text-muted">–</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>

    </template>

    <!-- Branch Drawer -->
    <branch-drawer
      :visible="drawerOpen"
      :branch-url="activeBranchUrl"
      :branch-name="activeBranchName"
      @change="drawerOpen = $event"
      @open-full="openFull"
    />

  </div>
</template>

<script>
import moment from "moment";
import { formatNumber } from "@/utils/utils";
import BarChart from "@/components/chart/BarChart.vue";
import PieChart from "@/components/chart/PieChart.vue";
import TrendBadge from "@/components/dashboard/TrendBadge.vue";
import BranchDrawer from "@/components/dashboard/BranchDrawer.vue";

const CASE_MIX_COLORS = [
  "#2196F3", "#26C6DA", "#8B5CF6", "#F59E0B", "#EF4444", "#94A3B8",
  "#22C55E", "#F97316", "#EC4899", "#64748B",
];

export default {
  name: "ClinicDashboard",

  components: { BarChart, PieChart, TrendBadge, BranchDrawer },

  data() {
    return {
      c: null,
      error: null,
      loading: false,

      // Range picker (mirrors BranchAnalysis.vue)
      viewType: "month",
      selectedMonth: moment().format("YYYY-MM"),
      selectedYear: moment().format("YYYY"),
      monthOptions: this.buildMonthOptions(),
      yearOptions: this.buildYearOptions(),

      // Comparison toggle: 'prevPeriod' | 'prevYear' — CLIENT-SIDE only (both pcts in payload)
      comparison: "prevPeriod",

      // Drawer state
      drawerOpen: false,
      activeBranchUrl: null,
      activeBranchName: "",
    };
  },

  computed: {
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },

    // ---- Date range (mirrors BranchAnalysis.vue) ----
    startDt() {
      if (this.viewType === "year") {
        return moment(this.selectedYear).startOf("year").format();
      }
      return this.selectedMonth
        ? moment(this.selectedMonth).startOf("month").format()
        : "";
    },
    endDt() {
      if (this.viewType === "year") {
        return moment(this.selectedYear).endOf("year").format();
      }
      return this.selectedMonth
        ? moment(this.selectedMonth).endOf("month").format()
        : "";
    },

    branchCount() {
      return this.c && this.c.branches ? this.c.branches.length : 0;
    },

    // branchUrl -> branch name, for alert chips (alerts carry only branchUrl)
    branchNameByUrl() {
      const map = {};
      if (this.c && this.c.branches) {
        for (const b of this.c.branches) map[b.branchUrl] = b.name;
      }
      return map;
    },

    // ---- Range / comparison labels ----
    rangeLabel() {
      const r = this.c && this.c.range;
      const start = r && r.startDt ? r.startDt : this.startDt;
      const end = r && r.endDt ? r.endDt : this.endDt;
      return this.formatRange(start, end);
    },
    comparisonLabel() {
      if (!this.c || !this.c.comparison) return "";
      const cmp = this.c.comparison[this.comparison];
      if (!cmp) return "";
      const suffix = this.comparison === "prevYear" ? " (ปีก่อน)" : " (ช่วงก่อนหน้า)";
      return this.formatRange(cmp.startDt, cmp.endDt) + suffix;
    },
    comparisonTag() {
      return this.comparison === "prevYear" ? "vs ปีก่อน" : "vs ช่วงก่อน";
    },

    // ---- Active comparison pct for each KPI ----
    revenuePct() {
      if (!this.c || !this.c.summary) return null;
      return this.c.summary.revenue[this.comparison + "Pct"];
    },
    perCasePct() {
      if (!this.c || !this.c.summary) return null;
      return this.c.summary.perCase[this.comparison + "Pct"];
    },
    noShowPctText() {
      if (!this.c || !this.c.summary || this.c.summary.noShow.pct == null) return "–";
      return this.c.summary.noShow.pct.toFixed(1);
    },

    // ---- Branches sorted by revenue ----
    sortedBranches() {
      if (!this.c || !this.c.branches) return [];
      return [...this.c.branches].sort(
        (a, b) => ((b.revenue && b.revenue.value) || 0) - ((a.revenue && a.revenue.value) || 0)
      );
    },

    // ---- Trend chart (Chart.js bar — labels map date->label; daily vs monthly handled by date length) ----
    trendChartData() {
      if (!this.c || !this.c.trend || !this.c.trend.length) return null;
      const pts = this.c.trend;
      const labels = pts.map((p) => this.trendLabel(p.date));
      const revenues = pts.map((p) => p.revenue || 0);
      return {
        labels,
        datasets: [
          {
            label: "รายได้ (฿)",
            data: revenues,
            backgroundColor: "#2196F3",
            borderColor: "#1976D2",
            borderWidth: 0,
            barPercentage: 0.7,
            categoryPercentage: 0.8,
          },
        ],
      };
    },
    trendChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: (tooltipItem) => "฿" + formatNumber(tooltipItem.yLabel || 0),
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: { display: false },
              ticks: { fontColor: "#6B7280", fontSize: 9, maxTicksLimit: 12, autoSkip: true },
            },
          ],
          yAxes: [
            {
              gridLines: { color: "#E5EAF0" },
              ticks: {
                fontColor: "#6B7280",
                fontSize: 10,
                beginAtZero: true,
                callback: (v) => {
                  if (v >= 1000000) return (v / 1000000).toFixed(1) + "M";
                  if (v >= 1000) return (v / 1000).toFixed(0) + "K";
                  return v;
                },
              },
            },
          ],
        },
      };
    },
    trendSubLabel() {
      const monthly = this.isMonthlyTrend;
      return monthly ? "รายได้รวมทุกสาขา · รายเดือน" : "รายได้รวมทุกสาขา · รายวัน";
    },
    isMonthlyTrend() {
      // Monthly trend dates are "YYYY-MM" (length 7); daily are "YYYY-MM-DD" (length 10)
      return !!(
        this.c &&
        this.c.trend &&
        this.c.trend.length &&
        this.c.trend[0].date &&
        this.c.trend[0].date.length === 7
      );
    },

    // ---- Case mix (donut) ----
    caseMixItems() {
      if (!this.c || !this.c.caseMix || !this.c.caseMix.length) return null;
      return this.c.caseMix.map((item, i) => ({
        ...item,
        color: CASE_MIX_COLORS[i % CASE_MIX_COLORS.length],
      }));
    },
    caseMixChartData() {
      const items = this.caseMixItems || [];
      return {
        labels: items.map((c) => c.category),
        datasets: [
          {
            data: items.map((c) => c.pct),
            backgroundColor: items.map((c) => c.color),
            borderWidth: 2,
            borderColor: "#fff",
          },
        ],
      };
    },
    caseMixOptions() {
      return {
        responsive: true,
        // Keep the donut square and sized by width only. With maintainAspectRatio
        // false the canvas tracks its (flex-fill) parent's height and balloons
        // past the 150px box, drifting below the legend.
        maintainAspectRatio: true,
        aspectRatio: 1,
        cutoutPercentage: 58,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.labels[tooltipItem.index] || "";
              const val = data.datasets[0].data[tooltipItem.index];
              return label + ": " + val + "%";
            },
          },
        },
      };
    },

    // ---- Top treatments bar max ----
    maxTreatmentRevenue() {
      if (!this.c || !this.c.topTreatments || !this.c.topTreatments.length) return 1;
      return Math.max(...this.c.topTreatments.map((t) => t.revenue || 0)) || 1;
    },

    // ---- Referral bar max ----
    maxReferralCount() {
      if (!this.c || !this.c.referralSources || !this.c.referralSources.length) return 1;
      return Math.max(...this.c.referralSources.map((s) => s.count || 0)) || 1;
    },
  },

  methods: {
    // ---- Picker option builders ----
    buildMonthOptions() {
      moment.locale("th");
      const options = [];
      for (let i = 0; i < 24; i++) {
        const date = moment().subtract(i, "months");
        options.push({
          value: date.format("YYYY-MM"),
          text: date.format("MMMM YYYY"),
        });
      }
      return options;
    },
    buildYearOptions() {
      const years = [];
      const currentYear = moment().year();
      for (let i = 0; i < 3; i++) {
        const year = currentYear - i;
        years.push({ value: year.toString(), text: `ปี ${year + 543}` });
      }
      return years;
    },

    setViewType(type) {
      if (this.viewType === type) return;
      this.viewType = type;
      // Reset selection to current period for the new view type
      if (type === "month") {
        this.selectedMonth = moment().format("YYYY-MM");
      } else {
        this.selectedYear = moment().format("YYYY");
      }
      // startDt/endDt watcher triggers the refetch
    },

    async load() {
      this.error = null;
      this.loading = true;
      try {
        this.c = await this.$store.dispatch("dashboard/fetchClinicDashboard", {
          clinicUrl: this.clinicUrl,
          startDt: this.startDt,
          endDt: this.endDt,
        });
      } catch (e) {
        this.error = e;
        this.$bvToast.toast("โหลดข้อมูลแดชบอร์ดไม่สำเร็จ", {
          variant: "danger",
          title: "ข้อผิดพลาด",
          autoHideDelay: 4000,
        });
      } finally {
        this.loading = false;
      }
    },

    openDrawer(branchUrl, branchName) {
      this.activeBranchUrl = branchUrl;
      this.activeBranchName = branchName || "";
      this.drawerOpen = true;
    },

    openFull(branchUrl) {
      this.$router.push({
        name: "CentralBranchDashboard",
        params: { clinicUrl: this.clinicUrl, branchUrl, branchName: this.activeBranchName },
      });
    },

    // ---- Alert helpers ----
    // Alerts: { type:"REVENUE_DROP"|"NO_SHOW"|"LOW_UTIL", severity:"red"|"orange", branchUrl, value }
    alertLabel(alert) {
      const name = this.branchNameByUrl[alert.branchUrl];
      const prefix = name ? name + " " : "";
      switch (alert.type) {
        case "REVENUE_DROP":
          return prefix + "รายได้ −" + Math.abs(alert.value || 0).toFixed(1) + "% " + this.comparisonTag;
        case "NO_SHOW":
          return prefix + "No-show " + (alert.value || 0).toFixed(1) + "%";
        case "LOW_UTIL":
          return prefix + "ใช้ยูนิตเฉลี่ย " + (alert.value || 0) + "%";
        default:
          return name || "";
      }
    },
    alertIcon(alert) {
      return alert.type === "REVENUE_DROP" ? "▼" : "⚠";
    },
    alertChipClass(alert) {
      return alert.severity === "red" ? "alert-chip--red" : "alert-chip--orange";
    },

    // ---- Branch row helpers ----
    branchRevenuePct(branch) {
      if (!branch.revenue) return null;
      const v = branch.revenue[this.comparison + "Pct"];
      return v == null ? null : v;
    },
    noShowPillClass(pct) {
      const v = pct || 0;
      return v >= 5 ? "pill--bad" : v >= 4 ? "pill--warn" : "pill--ok";
    },
    utilPillClass(pct) {
      const v = pct || 0;
      return v < 50 ? "pill--bad" : v < 60 ? "pill--warn" : "pill--ok";
    },
    doctorInitials(name) {
      if (!name) return "";
      // Take the last whitespace-separated token, first 2 chars (mockup behaviour)
      const token = name.replace(/^.*\s/, "");
      return token.slice(0, 2);
    },

    // ---- Bar widths ----
    treatBarWidth(revenue) {
      return Math.round(((revenue || 0) / this.maxTreatmentRevenue) * 100);
    },
    referralBarWidth(count) {
      return Math.round(((count || 0) / this.maxReferralCount) * 100);
    },

    // ---- Trend label mapping (daily YYYY-MM-DD -> day number; monthly YYYY-MM -> month label) ----
    trendLabel(date) {
      if (!date) return "";
      if (date.length === 7) {
        // monthly
        return moment(date, "YYYY-MM").format("MMM");
      }
      // daily — show day-of-month
      return moment(date, "YYYY-MM-DD").format("D");
    },

    // ---- Formatting ----
    formatNumber(val) {
      return formatNumber(val || 0);
    },
    formatRange(startDt, endDt) {
      if (!startDt) return "";
      const start = moment(startDt);
      const end = endDt ? moment(endDt) : null;
      if (this.viewType === "year" || (end && start.year() === end.year() && start.month() === 0 && end.month() === 11)) {
        return "ปี " + (start.year() + 543);
      }
      if (end && start.isSame(end, "month")) {
        return start.locale("th").format("MMMM YYYY");
      }
      if (end) {
        return start.locale("th").format("D MMM") + " – " + end.locale("th").format("D MMM YYYY");
      }
      return start.locale("th").format("MMMM YYYY");
    },
  },

  created() {
    this.load();
  },

  watch: {
    // Re-fetch whenever the selected range changes (picker or view-type change).
    startDt() {
      this.load();
    },
    endDt() {
      this.load();
    },

    // Vue Router reuses this instance when only :clinicUrl changes — refetch for new clinic.
    clinicUrl() {
      this.c = null;
      this.load();
    },
  },
};
</script>

<style scoped>
/* ---- Brand tokens ---- */
.clinic-dashboard {
  --cd-primary: #2196f3;
  --cd-primary-dark: #1976d2;
  --cd-primary-light: #e3f2fd;
  --cd-green: #22c55e;
  --cd-red: #ef4444;
  --cd-orange: #f59e0b;
  --cd-purple: #8b5cf6;
  --cd-bg: #f5f8fb;
  --cd-border: #e5eaf0;
  --cd-text-muted: #6b7280;
  background: var(--cd-bg);
  min-height: calc(100vh - 56px);
}

/* ---- Utilities ---- */
.text-green        { color: var(--cd-green)  !important; }
.text-red          { color: var(--cd-red)    !important; }
.text-orange       { color: var(--cd-orange) !important; }
.text-primary-dark { color: var(--cd-primary-dark) !important; }
.label-muted       { color: var(--cd-text-muted); }
.font-weight-semibold { font-weight: 600; }

/* ---- Range picker (segmented control) ---- */
.cd-seg {
  display: inline-flex;
  background: #fff;
  border: 1px solid #d2dae6;
  border-radius: 9px;
  padding: 3px;
}
.cd-seg--ghost {
  border-color: var(--cd-border);
}
.cd-seg-btn {
  font-size: 12px;
  font-weight: 600;
  color: var(--cd-text-muted);
  background: none;
  border: 0;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.15s;
}
.cd-seg-btn:disabled { cursor: not-allowed; opacity: 0.6; }
.cd-seg-btn.on {
  background: var(--cd-primary);
  color: #fff;
}
.cd-seg--ghost .cd-seg-btn.on {
  background: var(--cd-primary-light);
  color: var(--cd-primary-dark);
}
.cd-period-select {
  width: auto;
  min-width: 150px;
  font-weight: 600;
}

/* ---- Alert chips ---- */
.alert-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;
}
.alert-chip:hover { opacity: 0.8; }
.alert-chip--red { background: #ef444412; color: var(--cd-red); }
.alert-chip--orange { background: #f59e0b15; color: var(--cd-orange); }

/* ---- KPI cards ---- */
.kpi-card {
  border: 1px solid var(--cd-border);
  border-radius: 12px;
}
.kpi-num {
  font-size: clamp(20px, 2.2vw, 30px);
  font-weight: 700;
  line-height: 1;
}

/* ---- Legend dot ---- */
.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

/* ---- Top treatments ---- */
.treat-row {
  display: grid;
  grid-template-columns: 22px 1fr;
  align-items: center;
  gap: 12px;
}
.treat-rank {
  font-weight: 700;
  color: var(--cd-text-muted);
  font-size: 13px;
  text-align: center;
}
.treat-track {
  height: 7px;
  border-radius: 6px;
  background: var(--cd-bg);
  overflow: hidden;
}
.treat-fill {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #2196f3, #5b8dff);
  transition: width 0.4s ease;
}

/* ---- Referral bar ---- */
.referral-name {
  flex: 0 0 96px;
  color: var(--cd-text-muted);
}
.referral-bar-track {
  position: relative;
  border-radius: 999px;
  background: var(--cd-bg);
  height: 7px;
  overflow: hidden;
}
.referral-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #0f9d72, #3ec79a);
  transition: width 0.4s ease;
}

/* ---- Branches table ---- */
.cd-branch-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.cd-branch-table thead th {
  text-align: right;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--cd-text-muted);
  padding: 10px 16px;
  border-top: 1px solid var(--cd-border);
  border-bottom: 1px solid var(--cd-border);
  background: var(--cd-bg);
}
.cd-branch-table thead th.text-left { text-align: left; }
.cd-branch-table tbody td {
  padding: 13px 16px;
  text-align: right;
  border-bottom: 1px solid var(--cd-border);
  font-variant-numeric: tabular-nums;
}
.cd-branch-table tbody td.text-left { text-align: left; }
.cd-branch-table tbody tr:last-child td { border-bottom: 0; }
.cd-branch-row { cursor: pointer; transition: background 0.12s; }
.cd-branch-row:hover { background: var(--cd-bg); }
.bname { font-weight: 600; color: #1f2a37; }
.bsub { font-size: 11.5px; color: var(--cd-text-muted); font-weight: 400; }
.money { font-weight: 700; }
.branch-delta {
  font-size: 11.5px;
  font-weight: 600;
  display: inline-block;
  margin-left: 6px;
}
.pill {
  display: inline-block;
  font-weight: 600;
  font-size: 12px;
  padding: 3px 9px;
  border-radius: 7px;
}
.pill--ok { background: #e6f6f0; color: #0f9d72; }
.pill--warn { background: #fdf3e2; color: var(--cd-orange); }
.pill--bad { background: #fdece8; color: var(--cd-red); }
.doc { display: inline-flex; align-items: center; gap: 7px; }
.doc-av {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #dfe7f5;
  color: var(--cd-primary-dark);
  display: grid;
  place-items: center;
  font-size: 10.5px;
  font-weight: 700;
}
</style>
